import { connectInternalApi } from 'common/api'

interface LanguageRuleExample {
  example_input: string
  example_output: string
}

interface LanguageRuleResponse {
  _id: string
  iso_language: string
  rule: string
  examples: LanguageRuleExample[]
  version: number
  created: string
  last_updated?: string
  last_updated_by_name?: string
}

export interface LanguageRule {
  id: string
  isoLanguage: string
  rule: string
  examples: { exampleInput: string; exampleOutput: string }[]
  version: number
  created: string
  lastUpdated?: string
  lastUpdatedByName?: string
}

const mapLanguageRuleResponseToLanguageRule = (
  response: LanguageRuleResponse
): LanguageRule => ({
  id: response._id,
  isoLanguage: response.iso_language,
  rule: response.rule,
  examples: response.examples.map((example) => ({
    exampleInput: example.example_input,
    exampleOutput: example.example_output,
  })),
  version: response.version,
  created: response.created,
  lastUpdated: response.last_updated,
  lastUpdatedByName: response.last_updated_by_name ?? '',
})

export const fetchLanguageRules = async ({
  accountId,
}: {
  accountId: string
}) => {
  const response = await connectInternalApi.get<LanguageRuleResponse[]>(
    `v1/core/monorail/accounts/${accountId}/language-rules/`
  )

  return response.data.map(mapLanguageRuleResponseToLanguageRule)
}

type AddLanguageRuleBody = Pick<
  LanguageRule,
  'isoLanguage' | 'rule' | 'examples'
>

export const createLanguageRule = async ({
  accountId,
  data,
}: {
  accountId: string
  data: AddLanguageRuleBody
}) => {
  const payload = {
    iso_language: data.isoLanguage,
    rule: data.rule,
    examples: data.examples.map((example) => ({
      example_input: example.exampleInput,
      example_output: example.exampleOutput,
    })),
  }
  await connectInternalApi.post(
    `v1/core/monorail/accounts/${accountId}/language-rules/`,
    payload
  )
}

type EditLanguageRuleBody = Pick<LanguageRule, 'rule' | 'examples'>

export const editLanguageRule = async ({
  ruleId,
  accountId,
  data,
}: {
  ruleId: string
  accountId: string
  data: EditLanguageRuleBody
}) => {
  const payload = {
    rule: data.rule,
    examples: data.examples.map((example) => ({
      example_input: example.exampleInput,
      example_output: example.exampleOutput,
    })),
  }
  await connectInternalApi.patch(
    `v1/core/monorail/accounts/${accountId}/language-rules/${ruleId}`,
    payload
  )
}

export const deleteLanguageRule = async ({
  ruleId,
  accountId,
}: {
  ruleId: string
  accountId: string
}) => {
  await connectInternalApi.delete(
    `v1/core/monorail/accounts/${accountId}/language-rules/${ruleId}/`
  )
}
